import React, { useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { AxiosResponse, CancelToken } from 'axios';
import { useQueryParam } from '@domain/hooks/use.query';
import { FormDataConfig, useFormData } from '@domain/hooks/use.form.data';
import { isEqual } from '@domain/utils/object.utils';
import { createUrl } from '@domain/utils/url.utils';
import { noop } from '@domain/utils/function.utils';
import { getStalledPromise } from '@domain/utils/promise.utils';

export interface PageDataConfig<T, R> extends FormDataConfig<T, R> {
  placeholderData?: () => R;
  paramMap?: (data: T) => any;
}

export function usePagedData<T, R>(
  memoizedInitialData: T,
  requestFn: (data: T, page: number, cancelToken: CancelToken | undefined) => Promise<AxiosResponse<R>>,
  config: PageDataConfig<T, R> = {},
) {
  const { placeholderData, paramMap, submitCondition, autoRequest = true, clearResponseOnError = true, clearResponseOnRequest = false } = config;

  const history = useHistory();
  const { url } = useRouteMatch();
  const page = parseInt(useQueryParam('page') || '1', 10);

  async function runRequest(data: T, cancelToken: CancelToken | undefined) {
    return requestFn(data, page, cancelToken).catch((error) => {
      if (error && error.response && error.response.status === 404) {
        if (typeof placeholderData === 'function') {
          return { ...error.response, data: placeholderData() } as AxiosResponse<R>;
        }
        throw new Error('No data found');
      }
      throw error;
    });
  }

  function historyHandler(d: T) {
    if (submitCondition) {
      const conditionResult = submitCondition(d);
      if (!conditionResult) return conditionResult;
    }

    if (!isEqual(memoizedInitialData, d) || page > 1) {
      history.push(createUrl(url, paramMap ? paramMap(d) : undefined));
      return undefined;
    }

    return true;
  }

  const formData = useFormData(memoizedInitialData, runRequest, {
    submitCondition: historyHandler,
    autoRequest: false,
    clearResponseOnError,
    clearResponseOnRequest,
  });

  const { response, request, abort, setValues, data } = formData;
  const responseData = response?.data;
  const pageCount = parseInt(response?.headers?.totalpages || '1', 10);

  if (autoRequest) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!isEqual(memoizedInitialData, data)) {
        setValues(memoizedInitialData);
      }
      request(memoizedInitialData);
      return abort;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memoizedInitialData, page]);
  }

  return {
    ...formData,
    responseData,
    page,
    pageCount,
  };
}
